/*
 * Ajout automatique d'un produit au panier selon un seuil sur une collection.
 *
 * Piloté par les réglages du thème (voir snippet `auto-add-product.liquid` qui
 * expose `window.AAP_CONFIG`). Lorsque le montant des articles du panier
 * appartenant à la collection Y atteint le seuil X, le produit Z est ajouté
 * automatiquement. Si le panier repasse sous le seuil, Z est retiré.
 *
 * S'appuie sur le système pub/sub de Dawn (`subscribe` / `publish` /
 * `PUB_SUB_EVENTS`) : les composants natifs (cart-drawer-items, cart-items…)
 * se rafraîchissent seuls sur l'événement `cart-update`.
 */
(function () {
  const cfg = window.AAP_CONFIG;
  if (!cfg || !cfg.enabled || !cfg.giftAvailable) return;

  // Propriété de ligne qui identifie l'exemplaire ajouté automatiquement.
  const GIFT_PROP = '_aap';
  const MAX_PAGES = 250; // garde-fou pour la pagination de la collection

  let inFlight = false; // une modification panier est en cours
  let rerun = false; // une nouvelle vérification a été demandée pendant le traitement
  let collectionIds = null; // Set des product_id de la collection Y

  // Marqueur persistant : indique que l'exemplaire de Z présent dans le panier
  // a bien été ajouté par le script (donc retirable). Sert de repli fiable au
  // cas où la propriété de ligne masquée `_aap` ne serait pas relue.
  const MANAGED_KEY = 'aap_managed_' + cfg.giftVariantId;
  function isManaged() {
    try {
      return localStorage.getItem(MANAGED_KEY) === '1';
    } catch (e) {
      return false;
    }
  }
  function setManaged(on) {
    try {
      if (on) localStorage.setItem(MANAGED_KEY, '1');
      else localStorage.removeItem(MANAGED_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
  const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';

  /* ------------------------------------------------------------------ */
  /* Seuil converti dans la devise d'affichage de l'internaute           */
  /* ------------------------------------------------------------------ */
  // Taux boutique -> devise active, exposé par Shopify (Markets / multi-devise).
  function getCurrencyRate() {
    const raw =
      window.Shopify && window.Shopify.currency ? parseFloat(window.Shopify.currency.rate) : NaN;
    return raw && raw > 0 ? raw : 1;
  }

  // Renvoie le seuil (en centimes) exprimé dans la devise du panier fourni.
  // `cartCurrency` = cart.currency (devise d'affichage de l'internaute).
  function thresholdInCartCurrency(cartCurrency) {
    // Même devise que la boutique -> aucune conversion.
    if (!cartCurrency || !cfg.shopCurrency || cartCurrency === cfg.shopCurrency) {
      return cfg.thresholdCents;
    }
    // Les montants du panier et le seuil sont tous deux en centimes (×100),
    // le taux s'applique donc directement.
    return Math.round(cfg.thresholdCents * getCurrencyRate());
  }

  /* ------------------------------------------------------------------ */
  /* Récupération (et cache) des IDs produits de la collection Y         */
  /* ------------------------------------------------------------------ */
  async function getCollectionIds() {
    if (collectionIds) return collectionIds;

    const cacheKey = 'aap_ids_' + cfg.collectionHandle;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        collectionIds = new Set(JSON.parse(cached));
        return collectionIds;
      }
    } catch (e) {
      /* sessionStorage indisponible : on continue sans cache */
    }

    const ids = [];
    for (let page = 1; page <= MAX_PAGES; page++) {
      let data;
      try {
        const res = await fetch(
          `/collections/${cfg.collectionHandle}/products.json?limit=250&page=${page}`
        );
        if (!res.ok) break;
        data = await res.json();
      } catch (e) {
        break;
      }
      const products = (data && data.products) || [];
      if (products.length === 0) break;
      products.forEach((p) => ids.push(p.id));
    }

    collectionIds = new Set(ids);
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(ids));
    } catch (e) {
      /* ignore */
    }
    return collectionIds;
  }

  /* ------------------------------------------------------------------ */
  /* Rafraîchissement de l'UI                                            */
  /* ------------------------------------------------------------------ */
  async function refreshCartIconBubble() {
    try {
      const html = await (
        await fetch(`${cartUrl}?section_id=cart-icon-bubble`)
      ).text();
      const target = document.getElementById('cart-icon-bubble');
      if (!target) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector('.shopify-section') || doc.body;
      target.innerHTML = source.innerHTML;
    } catch (e) {
      /* ignore */
    }
  }

  async function refreshUI() {
    await refreshCartIconBubble();
    // Les composants natifs de Dawn se rafraîchissent sur cet événement
    // (source différente de 'cart-items' et de la nôtre).
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'auto-add-product' });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Ajout / retrait du produit Z                                        */
  /* ------------------------------------------------------------------ */
  async function addGift() {
    const body = {
      items: [
        {
          id: cfg.giftVariantId,
          quantity: 1,
          properties: { [GIFT_PROP]: '1' },
        },
      ],
    };
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return res.ok;
  }

  async function removeGift(lineKey) {
    const res = await fetch(changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });
    return res.ok;
  }

  /* ------------------------------------------------------------------ */
  /* Vérification principale                                             */
  /* ------------------------------------------------------------------ */
  async function evaluate() {
    if (inFlight) {
      rerun = true;
      return;
    }
    inFlight = true;

    try {
      const ids = await getCollectionIds();

      let cart;
      try {
        cart = await (await fetch(`${cartUrl}.js`)).json();
      } catch (e) {
        return;
      }
      if (!cart || !Array.isArray(cart.items)) return;

      let eligibleTotal = 0; // en centimes
      let giftPresent = false; // un exemplaire de Z est dans le panier
      let giftKey = null; // clé de la ligne du cadeau
      let giftIsOurs = false; // la ligne porte la propriété _aap

      cart.items.forEach((item) => {
        if (item.variant_id === cfg.giftVariantId) {
          giftPresent = true;
          giftKey = item.key;
          if (
            item.properties &&
            (item.properties[GIFT_PROP] === '1' || item.properties[GIFT_PROP] === true)
          ) {
            giftIsOurs = true;
          }
          return; // le cadeau ne compte jamais dans le seuil
        }

        if (ids.has(item.product_id)) {
          eligibleTotal += item.final_line_price;
        }
      });

      const thresholdCents = thresholdInCartCurrency(cart.currency);
      const qualifies = eligibleTotal >= thresholdCents;

      if (qualifies && !giftPresent) {
        // Seuil atteint et cadeau absent → on l'ajoute.
        if (await addGift()) {
          setManaged(true);
          await refreshUI();
        }
      } else if (!qualifies && giftPresent && (giftIsOurs || isManaged())) {
        // Sous le seuil et cadeau ajouté par le script → on le retire.
        if (await removeGift(giftKey)) {
          setManaged(false);
          await refreshUI();
        }
      } else if (!giftPresent) {
        // Plus aucun cadeau dans le panier : rien à gérer.
        setManaged(false);
      }
    } finally {
      inFlight = false;
      if (rerun) {
        rerun = false;
        evaluate();
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Abonnements                                                         */
  /* ------------------------------------------------------------------ */
  function boot() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Ignore les événements que l'on émet nous-mêmes pour éviter les boucles.
        if (event && event.source === 'auto-add-product') return;
        evaluate();
      });
    }
    // Vérification au chargement (panier déjà rempli, retour sur le site…).
    evaluate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
